import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { useMutation } from '@apollo/react-hooks';
import Button from '../Button';
import {
  REGISTER_USER,
  CREATE_ORGANIZATION,
  CREATE_STRIPE_CUSTOMER,
  ACTIVATE_SMS_CAMPAIGNS,
  NOTIFY_ACCOUNT_SETUP_ERROR,
} from '../../graphql/queries';
import { getErrorMessage } from '../../util/ErrorUtil';
import NewAccountScreenLayout from './NewAccountScreenLayout';
import colors from '../../styles/colors';

import step6Image from '../../assets/images/new-account/06.jpg';

const STORAGE_KEYS = {
  step1: 'newAccount_step1',
  businessOwner: 'newAccount_businessOwner',
  step3: 'newAccount_step3',
  smsOptIn: 'newAccount_smsOptIn',
  paymentToken: 'newAccount_paymentToken',
};

const STEP_LABELS = {
  login: 'Create Check This Out Login',
  organization: 'Create Check This Out Organization',
  payment: 'Process Onboarding Payment',
  referral: 'Reserve Phone Number',
  sms: 'Activate SMS Campaigns',
};

const STATUS = {
  PENDING: 'Pending',
  NEXT: 'Next',
  IN_PROCESS: 'In process',
  READY: 'Ready',
  ERROR: 'Error',
};

function getSessionJson(key) {
  try {
    const raw = sessionStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function clearNewAccountSession() {
  try {
    Object.values(STORAGE_KEYS).forEach((k) => sessionStorage.removeItem(k));
  } catch (err) {
    // ignore
  }
}

const StepRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${colors.grey5};
  gap: 16px;
`;

const StepLabel = styled.span`
  font-size: 1.2rem;
  color: ${colors.grey3};
`;

const StepStatus = styled.span`
  font-size: 1.2rem;
  color: ${(p) => (p.status === STATUS.ERROR ? colors.red : colors.grey3)};
  flex-shrink: 0;
`;

const BodyText = styled.p`
  color: ${colors.grey3};
  font-size: 16px;
  line-height: 1.8em;
  margin-bottom: 1em;
`;

const ErrorBlock = styled.div`
  margin-top: 16px;
  padding: 16px;
  background: ${colors.cream};
  border: 1px solid ${colors.red};
  border-radius: 5px;
  color: ${colors.grey2};
  font-size: 1.2rem;
  line-height: 1.6;
`;

export default function FinalAccountCreation() {
  const [stepStatus, setStepStatus] = useState({
    login: STATUS.PENDING,
    organization: STATUS.PENDING,
    payment: STATUS.PENDING,
    referral: STATUS.PENDING,
    sms: STATUS.PENDING,
  });
  const [globalError, setGlobalError] = useState('');
  const [allComplete, setAllComplete] = useState(false);
  const [showSmsStep, setShowSmsStep] = useState(false);

  const [register] = useMutation(REGISTER_USER, {
    onError(err) {
      setStepStatus((s) => ({ ...s, login: STATUS.ERROR }));
      setGlobalError(getErrorMessage(err));
    },
  });

  const [createOrganization] = useMutation(CREATE_ORGANIZATION, {
    onError(err) {
      setStepStatus((s) => ({ ...s, organization: STATUS.ERROR }));
      setGlobalError(getErrorMessage(err));
    },
  });

  const [createStripeCustomer] = useMutation(CREATE_STRIPE_CUSTOMER, {
    onError(err) {
      setStepStatus((s) => ({
        ...s,
        payment: STATUS.ERROR,
        referral: STATUS.ERROR,
      }));
      setGlobalError(getErrorMessage(err));
    },
  });

  const [activateSmsCampaign] = useMutation(ACTIVATE_SMS_CAMPAIGNS, {
    onError(err) {
      setStepStatus((s) => ({ ...s, sms: STATUS.ERROR }));
      setGlobalError(getErrorMessage(err));
    },
  });

  const [notifyAccountSetupError] = useMutation(NOTIFY_ACCOUNT_SETUP_ERROR);

  useEffect(() => {
    setShowSmsStep(sessionStorage.getItem(STORAGE_KEYS.smsOptIn) === 'true');
  }, []);

  const sendErrorNotification = useCallback((errorMessage, step1, businessOwner, smsOptIn) => {
    const userContactInfo = step1
      ? [step1.email, step1.phoneNumber, [step1.firstName, step1.lastName].filter(Boolean).join(' ')].filter(Boolean).join(', ')
      : '(none)';
    const orgInfo = businessOwner ? JSON.stringify(businessOwner, null, 2) : '(none)';
    notifyAccountSetupError({
      variables: {
        input: {
          errorMessage: errorMessage || 'Unknown error',
          userContactInfo,
          orgInfo,
          smsCampaignActivationChoice: smsOptIn ? 'Yes' : 'No',
        },
      },
    }).catch(() => {});
  }, [notifyAccountSetupError]);

  const runSteps = useCallback(async () => {
    const step1 = getSessionJson(STORAGE_KEYS.step1);
    const businessOwner = getSessionJson(STORAGE_KEYS.businessOwner);
    const step3 = getSessionJson(STORAGE_KEYS.step3);
    const smsOptIn = sessionStorage.getItem(STORAGE_KEYS.smsOptIn) === 'true';
    const cardToken = sessionStorage.getItem(STORAGE_KEYS.paymentToken);

    if (!step1 || !businessOwner || !step3 || !cardToken) {
      setGlobalError('Missing registration data. Please start over from step 1.');
      return;
    }

    try {
      setStepStatus((s) => ({ ...s, login: STATUS.IN_PROCESS }));

      const registerResult = await register({
        variables: {
          email: step1.email,
          firstName: step1.firstName,
          lastName: step1.lastName,
          phoneNumber: step1.phoneNumber,
          password: step1.password,
        },
      });

      if (!registerResult?.data?.register?.token) {
        const errMsg = getErrorMessage(registerResult?.errors?.[0]) || 'User creation failed';
        sendErrorNotification(errMsg, step1, businessOwner, smsOptIn);
        setStepStatus((s) => ({ ...s, login: STATUS.ERROR }));
        setGlobalError(errMsg);
        return;
      }

      localStorage.setItem('CHECK_THIS_OUT_AUTH_TOKEN', registerResult.data.register.token);
      setStepStatus((s) => ({ ...s, login: STATUS.READY, organization: STATUS.IN_PROCESS }));

      const orgResult = await createOrganization({
        variables: {
          name: businessOwner.name || '',
          ownerName: businessOwner.ownerName || '',
          ownerEmail: businessOwner.ownerEmail || '',
          ownerPhoneNumber: businessOwner.ownerPhoneNumber || '',
          address: {
            address1: businessOwner.address1 || '',
            address2: businessOwner.address2 || '',
            city: businessOwner.city || '',
            state: businessOwner.state || '',
            zip: businessOwner.zip || '',
          },
          logoUrl: step3.logoUrl || '',
          posterImageUrl: step3.posterImageUrl || '',
          timezoneId: step3.timezoneId || '',
        },
      });

      if (!orgResult?.data?.createOrganization?.token) {
        const errMsg = getErrorMessage(orgResult?.errors?.[0]) || 'Organization creation failed';
        sendErrorNotification(errMsg, step1, businessOwner, smsOptIn);
        setStepStatus((s) => ({ ...s, organization: STATUS.ERROR }));
        setGlobalError(errMsg);
        return;
      }

      localStorage.setItem('CHECK_THIS_OUT_AUTH_TOKEN', orgResult.data.createOrganization.token);
      setStepStatus((s) => ({
        ...s, organization: STATUS.READY, payment: STATUS.IN_PROCESS, referral: STATUS.IN_PROCESS,
      }));

      const paymentResult = await createStripeCustomer({
        variables: { cardToken },
      });

      const paymentSucceeded = paymentResult?.data?.createStripeCustomer === true;
      const paymentHasErrors = paymentResult?.errors?.length > 0;
      if (!paymentSucceeded || paymentHasErrors) {
        const errObj = paymentResult?.errors?.[0];
        const rawErrText = errObj
          ? [errObj.message, errObj.extensions?.exception?.message].filter(Boolean).join(' ')
          : '';
        const isPlivoOrNumberError = /plivo|exception in (searching|buying) plivo number|reserv/i.test(rawErrText);
        let errMsg = paymentHasErrors ? getErrorMessage(errObj) : 'Payment could not be processed.';
        if (isPlivoOrNumberError) {
          errMsg = "Payment was successful, but we couldn't reserve your organization's phone number.";
        } else if (!errMsg && errObj?.extensions?.code) {
          errMsg = `Payment failed (${errObj.extensions.code}).`;
        }
        errMsg = errMsg || 'Payment could not be processed.';
        sendErrorNotification(errMsg, step1, businessOwner, smsOptIn);
        setStepStatus((s) => ({
          ...s,
          payment: STATUS.ERROR,
          referral: STATUS.ERROR,
        }));
        setGlobalError(errMsg);
        return;
      }

      setStepStatus((s) => ({
        ...s,
        payment: STATUS.READY,
        referral: STATUS.READY,
        sms: smsOptIn ? STATUS.IN_PROCESS : STATUS.READY,
      }));

      if (smsOptIn) {
        const smsResult = await activateSmsCampaign();
        const smsSucceeded = smsResult?.data?.activateSmsCampaign?.campaignPhoneNumber;
        const smsHasErrors = smsResult?.errors?.length > 0;
        if (!smsSucceeded || smsHasErrors) {
          const errObj = smsResult?.errors?.[0];
          let errMsg = smsHasErrors ? getErrorMessage(errObj) : 'SMS campaigns could not be activated.';
          errMsg = errMsg || 'SMS campaigns could not be activated.';
          sendErrorNotification(errMsg, step1, businessOwner, smsOptIn);
          setStepStatus((s) => ({ ...s, sms: STATUS.ERROR }));
          setGlobalError(errMsg);
          return;
        }
        setStepStatus((s) => ({ ...s, sms: STATUS.READY }));
      }

      /*
       * Project 2026-005 (E1, FR-5, D-10): No extra checklist mutation here. New orgs already have
       * steps 4–7 flags false from `createOrganization`. Steps 1–3 are always `true` in
       * `onboardingChecklistProgress` for Admin/SuperAdmin (resolver), not separate Mongo fields.
       */
      setAllComplete(true);
      clearNewAccountSession();
    } catch (err) {
      const errMsg = getErrorMessage(err) || 'Account setup failed';
      sendErrorNotification(errMsg, step1, businessOwner, smsOptIn);
      setGlobalError(errMsg);
    }
  }, [register, createOrganization, createStripeCustomer, activateSmsCampaign, sendErrorNotification]);

  useEffect(() => {
    runSteps();
  }, []);

  const handleGoToDashboard = () => {
    window.location.href = '/admin/dashboard';
  };

  const handleReturnToLogin = () => {
    window.location.href = '/admin/login';
  };

  const steps = [
    { key: 'login', label: STEP_LABELS.login },
    { key: 'organization', label: STEP_LABELS.organization },
    { key: 'payment', label: STEP_LABELS.payment },
    { key: 'referral', label: STEP_LABELS.referral },
    ...(showSmsStep ? [{ key: 'sms', label: STEP_LABELS.sms }] : []),
  ];

  const footer = globalError ? (
    <Button
      text="Return to Login"
      type="button"
      onClick={handleReturnToLogin}
      width="auto"
    />
  ) : (
    <Button
      text="Go to Dashboard"
      type="button"
      onClick={handleGoToDashboard}
      disabled={!allComplete}
      width="auto"
    />
  );

  return (
    <NewAccountScreenLayout
      stepNumber={6}
      screenImageSrc={step6Image}
      title="Check This Out Account Setup"
      showSignInLink={false}
      footer={footer}
    >
      <BodyText>
        Hold tight, we&apos;re getting everything set up for you. Please don&apos;t close this
        window until all steps are complete. When everything is ready, click the Go To Dashboard
        button below.
      </BodyText>

      {steps.map(({ key, label }) => (
        <StepRow key={key}>
          <StepLabel>{label}</StepLabel>
          <StepStatus status={stepStatus[key]}>{stepStatus[key]}</StepStatus>
        </StepRow>
      ))}

      {globalError && (
        <ErrorBlock>
          Account setup has encountered an error and we need to step in to help. We have been
          notified of your issue and will be in touch very soon. We apologize for this unexpected
          issue.
        </ErrorBlock>
      )}
    </NewAccountScreenLayout>
  );
}
